/**
 * KisiHavuzKarti.jsx — bir kişinin belge HAVUZU: türü henüz belli olmayan dosyalar. Backlog #250 (#146'nın
 * ön yüzü). Kişi detay sayfalarında (personel `PersonelDetayPage`, firma portalı `FirmaPersonelDetailPage`)
 * belgelerin altında durur.
 *
 * Havuz = file_type 92 + bir kişi bağı (kapı kuyruğundan farkı: orada kişi bağı yok, o yüzden ikisi
 * karışmaz). Hiçbir şey 92'yi saymaz. Kapı kuyruğunun aksine havuz dosyaları AÇILIR — önizleme var.
 *
 *   yükle     POST /personnel/{id}/havuz (çoklu, en fazla 50; kısmi başarı normal)
 *   ata       BelgeTasimaModal mod "havuzdan" → gerçek tip + zorunlu son geçerlilik tarihi
 *   sil       POST /personnel/{id}/havuz/sil — tek dosya ya da çoklu seçim (#269; backend #298): yalnız bu kişinin
 *             havuzundaki dosyalar silinir, bağlar deleted_records'a arşivlenir, dosya başına karar döner
 *   "Havuza al" tersi yön, her belge kutusunda (sayfa tarafında) — BelgeTasimaModal mod "havuza"
 *
 * Kapı kuyruğu sayfasındaki "yalnız kişiye ata, tip sonra" seçeneği bu görevde YOK: o sayfa ön yüzde
 * henüz yapılmadı.
 *
 * `salt` (#247): firmanın kişiyle ilişkisi bitmişse backend yazma uçlarına 403 verir; yükle / ata / sil
 * gizlenir, önizleme kalır.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";
import { Archive, Eye, FileText, Trash2, Upload } from "lucide-react";
import BelgeTasimaModal from "./BelgeTasimaModal";
import DosyaKaynakSecici from "../Common/DosyaKaynakSecici";
import { getKisiFileTypes } from "../../services/kisiAdminService";
import {
  dosyaHatasi, deleteHavuzDosyalari, fetchKisiDosyaOnizleme, getHavuz, uploadHavuz,
} from "../../services/personelBelgeService";
import { useTranslation } from "react-i18next";

const TOPLU_ADET = 8; // istek başına dosya — tek istekte ~25 MB üst sınırının altında kalmak için
const ADET_SINIRI = 50;
const SIL_ADET = 50; // silme isteği başına en fazla dosya (backend #298 sözleşmesi); havuz sayfalanmaz, 50'den fazlası parçalanır
const trTarih = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "—");

function OnizlemeModal({ personId, dosya, onClose }) {
  const { t } = useTranslation("components");
  const [onizleme, setOnizleme] = useState(null); // { url, type }
  const [hata, setHata] = useState(null);

  useEffect(() => {
    let ignore = false;
    let url = null;
    async function run() {
      try {
        const sonuc = await fetchKisiDosyaOnizleme(personId, dosya.fileId);
        url = sonuc.url;
        if (!ignore) setOnizleme(sonuc);
      } catch (e) {
        if (!ignore) setHata(e.message);
      }
    }
    run();
    return () => { ignore = true; if (url) URL.revokeObjectURL(url); };
  }, [personId, dosya.fileId]);

  const resim = onizleme?.type?.startsWith("image/");
  return (
    <Modal isOpen toggle={onClose} size="xl">
      <ModalHeader toggle={onClose}>{dosya.orijinalAd}</ModalHeader>
      <ModalBody style={{ minHeight: 320 }}>
        {hata && <Alert fade={false} color="danger" className="small">{hata}</Alert>}
        {!onizleme && !hata && <div className="text-center py-5"><Spinner /></div>}
        {onizleme && (resim ? (
          <img src={onizleme.url} alt={dosya.orijinalAd} style={{ maxWidth: "100%", display: "block", margin: "0 auto" }} />
        ) : (
          <iframe title={dosya.orijinalAd} src={onizleme.url} style={{ width: "100%", height: "70vh", border: 0 }} />
        ))}
      </ModalBody>
      {onizleme && (
        <ModalFooter>
          <Button color="light" tag="a" href={onizleme.url} target="_blank" rel="noopener noreferrer">{t("kisiHavuz.yeniSekmedeAc")}</Button>
        </ModalFooter>
      )}
    </Modal>
  );
}

// `staff` (#297): personel sayfası — havuzdan gerçek bir tipe atarken son geçerlilik tarihini personel girer; firma girişi tarih görmez,
// atanan dosya personel onaylayana kadar Beklemede kalır.
export default function KisiHavuzKarti({ personId, kisiAd = "", firmaId, onChanged, reloadToken = 0, salt = false, staff = false }) {
  const { t: tx } = useTranslation("components");
  const [dosyalar, setDosyalar] = useState([]);
  const [tipAdlari, setTipAdlari] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [sonuc, setSonuc] = useState(null); // { basarili, toplam, reddedilenler[] }
  const [yerelToken, setYerelToken] = useState(0);
  const [onizle, setOnizle] = useState(null);
  const [ata, setAta] = useState(null);
  const [sil, setSil] = useState([]); // onay bekleyen silme: dosya satırları (tek ya da çoklu)
  const [siliniyor, setSiliniyor] = useState(false);
  const [silSonuc, setSilSonuc] = useState(null); // { basarili, toplam, reddedilenler[] } — yalnız en az bir ret varsa
  const [secili, setSecili] = useState([]); // işaretli fileId'ler

  const yenile = useCallback(() => setYerelToken((t) => t + 1), []);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const rows = await getHavuz(personId);
        if (!ignore) {
          setDosyalar(rows);
          // Yeniden okumada artık havuzda olmayan (silinen, atanan, başka kişinin) dosyalar seçimde kalmaz.
          setSecili((onceki) => onceki.filter((id) => rows.some((r) => r.fileId === id)));
        }
      } catch (e) {
        if (!ignore) { setError(e.message); setDosyalar([]); setSecili([]); }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [personId, reloadToken, yerelToken]);

  // "Önceki konum" için tip adları — liste okunamazsa sayfa yine çalışır, yalnız tip numarası görünür.
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const rows = await getKisiFileTypes();
        if (!ignore) setTipAdlari(Object.fromEntries(rows.map((t) => [t.fileTypeId, t.fileTypeName])));
      } catch {
        /* sessiz */
      }
    }
    run();
    return () => { ignore = true; };
  }, []);

  async function dosyaSec(e) {
    const secilen = [...(e.target.files ?? [])];
    e.target.value = "";
    await dosyalariYukle(secilen);
  }

  // #727: personel tarafında Fotoğraf çek / Dosyadan yükle seçiminden gelen liste de aynı yoldan geçer.
  async function dosyalariYukle(secilen) {
    if (secilen.length === 0) return;
    if (secilen.length > ADET_SINIRI) {
      setError(tx("kisiHavuz.tekSeferdeEnFazlaDosya", { ADET_SINIRI }));
      return;
    }
    // İstemci tarafı ön denetim: kötü dosyalar hiç gönderilmez, sonuçta yine listelenir.
    const gecerli = [];
    const reddedilenler = [];
    secilen.forEach((f) => {
      const sorun = dosyaHatasi(f);
      if (sorun) reddedilenler.push({ orijinalAd: f.name, hata: sorun }); else gecerli.push(f);
    });
    setYukleniyor(true);
    setError(null);
    setSonuc(null);
    let basarili = 0;
    try {
      for (let i = 0; i < gecerli.length; i += TOPLU_ADET) {
        // eslint-disable-next-line no-await-in-loop
        const cevap = await uploadHavuz(personId, gecerli.slice(i, i + TOPLU_ADET));
        basarili += cevap?.basariliAdet ?? 0;
        reddedilenler.push(...(cevap?.reddedilenler ?? []));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSonuc({ basarili, toplam: secilen.length, reddedilenler });
      setYukleniyor(false);
      yenile();
      onChanged?.();
    }
  }

  // Tek dosya da çoklu seçim de aynı yoldan geçer (liste uzunluğu 1 ya da N). Kısmi başarı normal: reddedilen
  // dosyalar sonuç kutusunda nedeniyle listelenir ve seçili kalır; istek hiç cevap vermezse (ağ, 403 …)
  // işlenmemiş her dosya o hatayla reddedilmiş sayılır.
  async function dosyalariSil() {
    const hedefler = sil;
    const adlar = Object.fromEntries(hedefler.map((d) => [d.fileId, d.orijinalAd]));
    const reddedilenler = [];
    setSiliniyor(true);
    setError(null);
    setFlash(null);
    setSilSonuc(null);
    let i = 0; // başlamış parçanın ilk dosyası: hata verirse bu parça ve sonrakiler işlenmemiştir
    try {
      for (; i < hedefler.length; i += SIL_ADET) {
        // eslint-disable-next-line no-await-in-loop
        const cevap = await deleteHavuzDosyalari(personId, hedefler.slice(i, i + SIL_ADET).map((d) => d.fileId));
        (cevap?.reddedilenler ?? []).forEach((r) => reddedilenler.push({ ...r, orijinalAd: r.orijinalAd ?? adlar[r.fileId] }));
      }
    } catch (err) {
      hedefler.slice(i).forEach((d) => reddedilenler.push({ fileId: d.fileId, orijinalAd: d.orijinalAd, hata: err.message }));
    }
    const basarili = hedefler.length - reddedilenler.length;
    setSil([]);
    setSecili(reddedilenler.map((r) => r.fileId));
    if (reddedilenler.length === 0) {
      setFlash(hedefler.length === 1 ? "Dosya havuzdan silindi." : `${hedefler.length} dosya havuzdan silindi.`);
    } else {
      setSilSonuc({ basarili, toplam: hedefler.length, reddedilenler });
    }
    setSiliniyor(false);
    yenile();
    if (basarili > 0) onChanged?.();
  }

  const seciliSet = new Set(secili);
  const hepsiSecili = dosyalar.length > 0 && secili.length === dosyalar.length;
  const sec = (fileId) => setSecili((onceki) => (onceki.includes(fileId) ? onceki.filter((id) => id !== fileId) : [...onceki, fileId]));
  const hepsiniSec = () => setSecili(dosyalar.map((d) => d.fileId));

  const inputId = `havuz-yukle-${personId}`;
  const [kaynakAcik, setKaynakAcik] = useState(false);

  return (
    <Card className="mt-3">
      <CardHeader className="bg-transparent d-flex align-items-center gap-2">
        <Archive size={16} className="text-muted" />
        <span className="fw-semibold">{tx("kisiHavuz.havuz")}</span>
        <Badge color="light" className="text-body fw-normal">{dosyalar.length}</Badge>
        {!salt && (
          <>
            {staff ? (
              <DosyaKaynakSecici
                acik={kaynakAcik} onKapat={() => setKaynakAcik(false)} accept=".pdf,.jpg,.jpeg,.png"
                multiple onDosyalar={dosyalariYukle}
              />
            ) : (
              <input id={inputId} type="file" multiple className="d-none" accept=".pdf,.jpg,.jpeg,.png" onChange={dosyaSec} />
            )}
            <Button
              size="sm" color="primary" outline className="ms-auto" disabled={yukleniyor}
              onClick={() => (staff ? setKaynakAcik(true) : document.getElementById(inputId)?.click())}
            >
              {yukleniyor ? <Spinner size="sm" /> : <><Upload size={13} className="me-1" />{tx("kisiHavuz.dosyaYukle")}</>}
            </Button>
          </>
        )}
      </CardHeader>
      <CardBody>
        {flash && <Alert fade={false} color="success" toggle={() => setFlash(null)} className="py-2 small">{flash}</Alert>}
        {error && <Alert fade={false} color="danger" toggle={() => setError(null)} className="py-2 small">{error}</Alert>}
        {sonuc && (
          <Alert
            fade={false} color={sonuc.reddedilenler.length ? "warning" : "success"}
            toggle={() => setSonuc(null)} className="py-2 small"
          >
            {tx("kisiHavuz.dosyaHavuzaAlindi", { basarili: sonuc.basarili, toplam: sonuc.toplam })}
            {sonuc.reddedilenler.length > 0 && (
              <ul className="mb-0 mt-1">
                {sonuc.reddedilenler.map((r, i) => <li key={`${r.orijinalAd}-${i}`}><strong>{r.orijinalAd}</strong>: {r.hata}</li>)}
              </ul>
            )}
          </Alert>
        )}
        {silSonuc && (
          <Alert
            fade={false} color={silSonuc.basarili === 0 ? "danger" : "warning"}
            toggle={() => setSilSonuc(null)} className="py-2 small"
          >
            {tx("kisiHavuz.dosyaHavuzdanSilindi", { basarili: silSonuc.basarili, toplam: silSonuc.toplam })}
            <ul className="mb-0 mt-1">
              {silSonuc.reddedilenler.map((r, i) => (
                <li key={`${r.fileId}-${i}`}><strong>{r.orijinalAd || tx("kisiHavuz.dosya", { fileId: r.fileId })}</strong>: {r.hata}</li>
              ))}
            </ul>
          </Alert>
        )}

        {!salt && secili.length > 0 && (
          <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
            <span className="small text-muted">{secili.length}{" "}{tx("kisiHavuz.secili")}</span>
            {!hepsiSecili && <Button size="sm" color="light" onClick={hepsiniSec}>{tx("kisiHavuz.tumunuSec", { length: dosyalar.length })}</Button>}
            <Button size="sm" color="light" onClick={() => setSecili([])}>{tx("kisiHavuz.secimiTemizle")}</Button>
            <Button
              size="sm" color="danger" className="ms-auto" disabled={siliniyor}
              onClick={() => setSil(dosyalar.filter((d) => seciliSet.has(d.fileId)))}
            >
              <Trash2 size={13} className="me-1" />{tx("kisiHavuz.secilenleriSil", { length: secili.length })}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-3"><Spinner size="sm" /></div>
        ) : dosyalar.length === 0 ? (
          <div className="small text-muted">
            {salt ? tx("kisiHavuz.havuzBos") : tx("kisiHavuz.havuzBosTuruHenuzBelli")}
          </div>
        ) : (
          <div className="table-responsive">
            <Table size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  {!salt && (
                    <th style={{ width: 34 }}>
                      <Input
                        type="checkbox" checked={hepsiSecili} aria-label={tx("kisiHavuz.tumunuSec2")} title={tx("kisiHavuz.tumunuSec2")}
                        innerRef={(el) => { if (el) el.indeterminate = secili.length > 0 && !hepsiSecili; }}
                        onChange={() => (hepsiSecili ? setSecili([]) : hepsiniSec())}
                      />
                    </th>
                  )}
                  <th>{tx("kisiHavuz.dosya2")}</th>
                  <th>{tx("kisiHavuz.yukleyen")}</th>
                  <th>{tx("kisiHavuz.tarih")}</th>
                  <th>{tx("kisiHavuz.oncekiKonum")}</th>
                  <th className="text-end">{tx("kisiHavuz.islem")}</th>
                </tr>
              </thead>
              <tbody>
                {dosyalar.map((d) => (
                  <tr key={d.fileId} className={seciliSet.has(d.fileId) ? "table-active" : undefined}>
                    {!salt && (
                      <td>
                        <Input
                          type="checkbox" checked={seciliSet.has(d.fileId)} onChange={() => sec(d.fileId)}
                          aria-label={tx("kisiHavuz.dosyasiniSec", { orijinalAd: d.orijinalAd })}
                        />
                      </td>
                    )}
                    <td><FileText size={13} className="me-1 text-muted" />{d.orijinalAd}</td>
                    <td>{d.yukleyenAdi || "—"}</td>
                    <td>{trTarih(d.yuklemeTarihi)}</td>
                    <td>{d.oncekiFileTypeId ? tipAdlari[d.oncekiFileTypeId] ?? tx("kisiHavuz.tip", { oncekiFileTypeId: d.oncekiFileTypeId }) : "—"}</td>
                    <td className="text-end text-nowrap">
                      <Button size="sm" color="light" className="me-1" onClick={() => setOnizle(d)} title={tx("kisiHavuz.onizle")}>
                        <Eye size={13} />
                      </Button>
                      {!salt && (
                        <>
                          <Button size="sm" color="primary" outline className="me-1" onClick={() => setAta(d)}>
                            {tx("kisiHavuz.belgeTipineAta")}
                          </Button>
                          <Button size="sm" color="danger" outline onClick={() => setSil([d])} title={tx("common:sil")}>
                            <Trash2 size={13} />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>

      {onizle && <OnizlemeModal personId={personId} dosya={onizle} onClose={() => setOnizle(null)} />}

      {ata && (
        <BelgeTasimaModal
          mod="havuzdan" personId={personId} kisiAd={kisiAd} firmaId={firmaId} staff={staff}
          belgeAdi={ata.orijinalAd}
          doc={{ fileId: ata.fileId, fileName: ata.orijinalAd, fileTypeId: 92, expiryDate: null, reviewStatus: null }}
          onClose={() => setAta(null)}
          onDone={(sonuc) => {
            setAta(null);
            setFlash(sonuc?.incelemeBekliyor ? tx("kisiHavuz.dosyaBelgeTipineAtandiFdsBekliyor") : tx("kisiHavuz.dosyaBelgeTipineAtandi"));
            yenile();
            onChanged?.();
          }}
        />
      )}

      <Modal isOpen={sil.length > 0} toggle={siliniyor ? undefined : () => setSil([])}>
        <ModalHeader toggle={siliniyor ? undefined : () => setSil([])}>
          {sil.length > 1 ? tx("kisiHavuz.secilenDosyalariSil") : tx("kisiHavuz.dosyayiSil")}
        </ModalHeader>
        <ModalBody>
          {sil.length > 1 ? (
            <>
              <strong>{sil.length}{" "}{tx("kisiHavuz.dosya3")}</strong> {kisiAd ? tx("kisiHavuz.kisisinin", { kisiAd }) : tx("kisiHavuz.kisinin")}{" "}{tx("kisiHavuz.havuzundanSilinecek")}
              <ul className="small mt-2 mb-0" style={{ maxHeight: 180, overflowY: "auto" }}>
                {sil.map((d) => <li key={d.fileId}>{d.orijinalAd}</li>)}
              </ul>
            </>
          ) : (
            <><strong>{sil[0]?.orijinalAd}</strong> {kisiAd ? tx("kisiHavuz.kisisinin", { kisiAd }) : tx("kisiHavuz.kisinin")}{" "}{tx("kisiHavuz.havuzundanSilinecek")}</>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setSil([])} disabled={siliniyor}>{tx("kisiHavuz.vazgec")}</Button>
          <Button color="danger" onClick={dosyalariSil} disabled={siliniyor}>
            {siliniyor ? <Spinner size="sm" /> : sil.length > 1 ? tx("kisiHavuz.dosyayiSil2", { length: sil.length }) : tx("common:sil")}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}
